import React, { FunctionComponent, Fragment, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { BodySection, ActionSection } from 'components/UI/Section';
import Section from '../Section';
import Service, { StaffMember } from 'model/Service';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { ChipSelector, Element } from 'components/Service/ChipSelector';
import { showError } from '@spike/notifications-action';
import {
    buildSelectedStaffElements,
    buildServiceStaffFromMarketplaces,
    buildStaffElements
} from './utils/staffElements';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';

interface ServiceStaffProps {
    collapsed?: boolean;
    disabled?: boolean;
    editable?: boolean;
    completed?: boolean;
    modifiable?: boolean;
    service: Service;
    onEdit?: () => void;
    onChange: (staff: Array<StaffMember>) => void;
    onChangeMarketplaces?: (marketplaces: Service['marketplaces']) => void;
    onNext: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
        },
        titleContainer: {
            display: 'flex'
        },
        title: {
            fontWeight: 600,
            [theme.breakpoints.down('lg')]: {
                fontSize: '16px'
            },
            [theme.breakpoints.up('xl')]: {
                fontSize: '22px'
            }
        },
        staffMembersContainer: {
            [theme.breakpoints.down('lg')]: {
                marginTop: '16px'
            },
            [theme.breakpoints.up('xl')]: {
                marginTop: '24px'
            }
        },
        noneStaffDescriptionContainer: {
            display: 'flex',
            width: '100%',
            [theme.breakpoints.down('lg')]: {
                marginTop: '16px'
            },
            [theme.breakpoints.up('xl')]: {
                marginTop: '24px'
            }
        },
        noneStaffDescription: {
            [theme.breakpoints.down('lg')]: {
                fontSize: '12px'
            },
            [theme.breakpoints.up('xl')]: {
                fontSize: '16px'
            }
        },
        staffSectionTitle: {
            fontSize: 16,
            fontWeight: 600,
            color: '#000',
            margin: 0,

            [theme.breakpoints.up('xl')]: {
                fontSize: 20
            }
        },
        marketplaceStaffSection: {
            marginTop: 24,

            [theme.breakpoints.up('xl')]: {
                marginTop: 30
            }
        },
        marketplaceHeader: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            cursor: 'pointer',
            userSelect: 'none',
            paddingBottom: 12
        },
        marketplaceHeaderLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            minWidth: 0
        },
        marketplaceName: {
            fontSize: 16,
            fontWeight: 600,
            color: '#000',
            margin: 0,

            [theme.breakpoints.up('xl')]: {
                fontSize: 20
            }
        },
        marketplaceCount: {
            fontSize: 16,
            fontWeight: 600,
            color: '#000',
            margin: 0,
            whiteSpace: 'nowrap',

            [theme.breakpoints.up('xl')]: {
                fontSize: 20
            }
        },
        marketplaceChevron: {
            fontSize: 14,
            color: '#000',
            flexShrink: 0,

            [theme.breakpoints.up('xl')]: {
                fontSize: 16
            }
        },
        marketplaceNameDivider: {
            width: '100%',
            height: 1,
            backgroundColor: '#000'
        },
        marketplaceStaffContent: {
            marginTop: 22,

            [theme.breakpoints.up('xl')]: {
                marginTop: 30
            }
        },
        staffNotDefined: {
            fontSize: 14,
            fontWeight: 400,
            color: '#7A7A7A',
            margin: 0,

            [theme.breakpoints.up('md')]: {
                fontSize: 16
            }
        }
    })
);

export const ServiceStaff: FunctionComponent<ServiceStaffProps> = props => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { organizationServicesInfo } = useOrganizationMarketplaces();
    const [expandedMarketplaceIds, setExpandedMarketplaceIds] = useState<Array<number>>([]);

    const selectedMarketplaces = useMemo(
        () =>
            props.service.marketplaces
                .map(serviceMarketplace => {
                    const servicesInfo = organizationServicesInfo.find(
                        info => info.marketplace.id === serviceMarketplace.marketplaceId
                    );

                    if (!servicesInfo) {
                        return null;
                    }

                    return {
                        serviceMarketplace,
                        servicesInfo
                    };
                })
                .filter(
                    (
                        marketplace
                    ): marketplace is {
                        serviceMarketplace: Service['marketplaces'][number];
                        servicesInfo: (typeof organizationServicesInfo)[number];
                    } => marketplace !== null
                )
                .sort((left, right) =>
                    left.servicesInfo.marketplace.name
                        .toLocaleLowerCase()
                        .localeCompare(right.servicesInfo.marketplace.name.toLocaleLowerCase())
                ),
        [organizationServicesInfo, props.service.marketplaces]
    );

    const hasActiveStaffInOrganization = useMemo(
        () => organizationServicesInfo.some(info => info.staff.some(member => member.active)),
        [organizationServicesInfo]
    );

    // Auto-select all active staff for newly added marketplaces (create or edit).
    useEffect(() => {
        if (!props.onChangeMarketplaces || selectedMarketplaces.length === 0) {
            return;
        }

        const shouldAutoLoad = selectedMarketplaces.some(
            ({ serviceMarketplace, servicesInfo }) =>
                serviceMarketplace.staffIds.length === 0 && servicesInfo.staff.some(member => member.active)
        );

        if (!shouldAutoLoad) {
            return;
        }

        const updatedMarketplaces = props.service.marketplaces.map(marketplace => {
            const servicesInfo = organizationServicesInfo.find(
                info => info.marketplace.id === marketplace.marketplaceId
            );

            if (!servicesInfo || marketplace.staffIds.length > 0) {
                return marketplace;
            }

            return {
                ...marketplace,
                staffIds: servicesInfo.staff.filter(member => member.active).map(member => member.id)
            };
        });

        props.onChangeMarketplaces(updatedMarketplaces);
        props.onChange(buildServiceStaffFromMarketplaces(updatedMarketplaces, organizationServicesInfo));
    }, [
        organizationServicesInfo,
        props.onChange,
        props.onChangeMarketplaces,
        props.service.marketplaces,
        selectedMarketplaces
    ]);

    const marketplaceStaffSelectionChangeHandler =
        (marketplaceId: number) =>
        (elements: Array<Element<number>>): void => {
            if (!props.onChangeMarketplaces) {
                return;
            }

            if (elements.length === 0) {
                dispatch(showError('The service must have at least one staff assigned.'));
                return;
            }

            const updatedMarketplaces = props.service.marketplaces.map(marketplace =>
                marketplace.marketplaceId === marketplaceId
                    ? { ...marketplace, staffIds: elements.map(element => element.id) }
                    : marketplace
            );

            props.onChangeMarketplaces(updatedMarketplaces);
            props.onChange(buildServiceStaffFromMarketplaces(updatedMarketplaces, organizationServicesInfo));
        };

    const toggleMarketplaceExpanded = (marketplaceId: number) => {
        setExpandedMarketplaceIds(prev =>
            prev.includes(marketplaceId) ? prev.filter(id => id !== marketplaceId) : [...prev, marketplaceId]
        );
    };

    const addStaff = (
        <Fragment>
            <Box className={classes.titleContainer}>
                <Typography className={classes.title}>Add Staff Members</Typography>
            </Box>
            <Box className={classes.staffMembersContainer}>
                <Typography className={classes.staffSectionTitle}>Choose staff members for each location</Typography>
                {selectedMarketplaces.map(({ serviceMarketplace, servicesInfo }) => {
                    const activeMembers = servicesInfo.staff.filter(member => member.active);
                    const activeMemberIds = new Set(activeMembers.map(member => member.id));
                    const selectedCount = serviceMarketplace.staffIds.filter(staffId =>
                        activeMemberIds.has(staffId)
                    ).length;
                    const totalCount = activeMembers.length;
                    const isExpanded = expandedMarketplaceIds.includes(serviceMarketplace.marketplaceId);

                    return (
                        <Box
                            key={serviceMarketplace.marketplaceId}
                            className={classes.marketplaceStaffSection}
                        >
                            <Box
                                className={classes.marketplaceHeader}
                                onClick={() => toggleMarketplaceExpanded(serviceMarketplace.marketplaceId)}
                            >
                                <Box className={classes.marketplaceHeaderLeft}>
                                    <Typography className={classes.marketplaceName}>
                                        {servicesInfo.marketplace.name}
                                    </Typography>
                                    <Typography className={classes.marketplaceCount}>
                                        ({selectedCount}/{totalCount})
                                    </Typography>
                                </Box>
                                <FontAwesomeIcon
                                    icon={isExpanded ? faChevronUp : faChevronDown}
                                    className={classes.marketplaceChevron}
                                />
                            </Box>
                            <Box className={classes.marketplaceNameDivider} />
                            {isExpanded && (
                                <Box className={classes.marketplaceStaffContent}>
                                    {activeMembers.length > 0 ? (
                                        <ChipSelector
                                            id={`services_add_service_staff_selector_${serviceMarketplace.marketplaceId}`}
                                            searchLabel="Search Staff"
                                            allLabel="All Members"
                                            elements={buildStaffElements(servicesInfo.staff)}
                                            selectedElements={buildSelectedStaffElements(
                                                servicesInfo.staff,
                                                serviceMarketplace.staffIds
                                            )}
                                            changeSelection={marketplaceStaffSelectionChangeHandler(
                                                serviceMarketplace.marketplaceId
                                            )}
                                        />
                                    ) : (
                                        <Typography className={classes.staffNotDefined}>Staff not defined</Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Fragment>
    );

    const noneStaff = (
        <Fragment>
            <Box className={classes.titleContainer}>
                <Typography className={classes.title}>No Staff members have been created yet.</Typography>
            </Box>
            <Box className={classes.noneStaffDescriptionContainer}>
                <Typography className={classes.noneStaffDescription}>
                    You can come back to add staff once you create them to <br /> activate the service.
                </Typography>
            </Box>
        </Fragment>
    );

    return (
        <Section
            position={4}
            title="Staff"
            {...props}
            collapsed={props.collapsed}
        >
            <BodySection minHeigth="350px">
                <Box className={classes.container}>{hasActiveStaffInOrganization ? addStaff : noneStaff}</Box>
            </BodySection>
            <ActionSection
                disabled={props.disabled}
                buttonLabel="Save"
                id="services_add_service_staff_button_next"
                onNext={props.onNext}
            />
        </Section>
    );
};

export default ServiceStaff;
